import { useState, useRef, useEffect, useCallback } from 'react';

const TURNSTILE_SITE_KEY = '0x4AAAAAAC8CjREYvOZnHJtL';

const SubscribeForm = ({ dark = false, className = '' }) => {
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { text, type }
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  const resetTurnstile = useCallback(() => {
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      setTurnstileToken('');
    }
  }, []);

  useEffect(() => {
    const container = turnstileRef.current;
    if (!container) return;

    let interval = null;

    const renderWidget = () => {
      if (!window.turnstile || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(container, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        size: 'invisible',
      });
    };

    // Turnstile script may already be loaded or still loading
    if (window.turnstile) {
      renderWidget();
    } else {
      // Wait for the script to load
      interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          interval = null;
          renderWidget();
        }
      }, 200);
    }

    // Cleanup on unmount: remove the Turnstile widget so it stops running
    // in Cloudflare's iframe and frees the memory it was holding. Without
    // this, every route change leaves orphan widgets running indefinitely.
    return () => {
      if (interval) clearInterval(interval);
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Widget may already be gone; ignore.
        }
        widgetIdRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || loading) return;
    if (honeypot) return; // bot filled the hidden field

    setLoading(true);
    setMessage(null);
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, _hp: honeypot, _t: turnstileToken }),
      });

      const data = await res.json();

      if (res.ok) {
        if (data.existing) {
          setMessage({ text: "You're already subscribed. See you Tuesday!", type: 'info' });
        } else if (data.validating) {
          setMessage({ text: "Check your inbox to confirm your subscription.", type: 'success' });
        } else {
          setMessage({ text: "You're subscribed! Check your inbox.", type: 'success' });
        }
        setEmail('');
      } else {
        setMessage({ text: data.error || 'Something went wrong. Try again.', type: 'info' });
      }
    } catch {
      setMessage({ text: 'Network error. Please try again.', type: 'info' });
    } finally {
      setLoading(false);
      resetTurnstile();
      setTimeout(() => setMessage(null), 5000);
    }
  };

  return (
    <div className={`subscribe-wrapper ${className}`}>
      <form
        className={`subscribe-form ${dark ? 'subscribe-form--dark' : ''}`}
        onSubmit={handleSubmit}
        data-testid="subscribe-form"
      >
        {/* Honeypot — hidden from humans, bots fill it and get silently rejected */}
        <input
          type="text"
          name="company_url"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
        />
        <input
          type="email"
          placeholder="Your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="subscribe-input"
          data-testid="subscribe-email"
        />
        <button
          type="submit"
          className="subscribe-btn"
          disabled={loading}
          data-testid="subscribe-submit"
        >
          {loading ? 'Subscribing...' : 'Subscribe Free'}
        </button>
        {/* Turnstile invisible widget */}
        <div ref={turnstileRef} style={{ position: 'absolute', left: '-9999px' }} />
      </form>
      {message && (
        <div className={`subscribe-message subscribe-message--${message.type}`}>
          {message.text}
        </div>
      )}
    </div>
  );
};

export default SubscribeForm;
